// libs
import axios, { AxiosResponse } from 'axios';
import { SWRResponse } from 'swr';
// App
import { SHOP_OPERATIONS_SHOPS_URL } from '../shop.constants';
import { CheckoutAccessoriesReturnParam, OrderSavedEvent } from './order.interfaces';
import { useSWRAxiosGetRequest } from 'app-shared/lib/api/utils/swr-axios-request.hooks';
import { API_SHOP_OPERATIONS_BASE_URL } from 'app-shared/lib/api/utils/api.constants';
import { ORDER_STATUS } from 'app-shared/lib/api/shared/order.interfaces';
import useRSocket, { AxiosHookResponse } from 'app-shared/lib/api/utils/rsocket.hooks';
import { RSocketHookResponse } from 'app-shared/lib/api/utils/rsocket.hooks.interfaces';
import { useAxiosGetRequest, useAxiosPostRequest } from 'app-shared/lib/api/utils/axios-request.hooks';
import { AxiosHookResponse as AxiosRequestHookResponse } from 'app-shared/lib/api/utils/axios-request.hooks.interfaces';
import { Session } from 'app-shared/lib/api/user-access/auth';
import { Order } from '../../order/order.interfaces';

const SECOND = 1000;
const MINUTE = SECOND * 60;
const REFETCH_ORDERS_INTERVAL = 2 * MINUTE;

/**
 * Get shop api hook
 */
export const useGetShopOrders = (
  id?: number,
  startDate?: Date,
  endDate?: Date,
  status?: ORDER_STATUS,
  shouldFetch = true
): SWRResponse<Order[], Error> => {
  const idPart = (id && `id=${id}`) || '';
  const startDateQueryStringPart = (startDate && `&startDate=${startDate?.toISOString()}`) || '';
  const endDateQueryStringPart = (endDate && `&endDate=${endDate?.toISOString()}`) || '';
  const statusQueryStringPart = (status && `&status=${status}`) || '';

  const urlOptional = shouldFetch
    ? `${API_SHOP_OPERATIONS_BASE_URL}/${SHOP_OPERATIONS_SHOPS_URL}/orders?${idPart}${startDateQueryStringPart}${endDateQueryStringPart}${statusQueryStringPart}`
    : null;

  return useSWRAxiosGetRequest(urlOptional, { revalidateOnFocus: false, refreshInterval: REFETCH_ORDERS_INTERVAL });
};

const buildShopOrdersUrlOptional = (shopId: number): string | null => {
  if (!shopId) {
    return null;
  }

  const url = `${API_SHOP_OPERATIONS_BASE_URL}/${SHOP_OPERATIONS_SHOPS_URL}/${shopId}/orders`;

  return url;
};

/**
 * Get shop client orders paginated api hook, filterable by userUid through query params
 */
export const useGetShopClientOrders = (shopId: number): AxiosRequestHookResponse<void, Order[]> => {
  const urlOptional = buildShopOrdersUrlOptional(shopId);
  const shopClientOrdersResponse = useAxiosGetRequest<Order[]>(urlOptional);

  return shopClientOrdersResponse;
};

/**
 * Update order shop status api
 * @param id the id of the order shop
 * @param status the new status for the order shop
 */
export const updateOrderStatus = (id: number, status: ORDER_STATUS): Promise<AxiosResponse<Order>> => {
  return axios.put(
    `${API_SHOP_OPERATIONS_BASE_URL}/${SHOP_OPERATIONS_SHOPS_URL}/orders/${id}/status?status=${status}`
  );
};

/**
 * Increment order print count api
 * @param id the id of the order shop
 */
export const incrementOrderPrintCount = (id: number): Promise<AxiosResponse<Order>> => {
  return axios.post(`${API_SHOP_OPERATIONS_BASE_URL}/${SHOP_OPERATIONS_SHOPS_URL}/orders/${id}/printCount/increment`);
};

/**
 * Decrement order print count api
 * @param id the id of the order shop
 */
export const decrementOrderPrintCount = (id: number): Promise<AxiosResponse<Order>> => {
  return axios.post(`${API_SHOP_OPERATIONS_BASE_URL}/${SHOP_OPERATIONS_SHOPS_URL}/orders/${id}/printCount/decrement`);
};

/**
 * Listens to shop orders events
 * @param id the id of the shop
 */
export const useGetShopOrdersEvents = (
  shopId: number,
  session: Session,
  shouldListen = true
): RSocketHookResponse<OrderSavedEvent> => {
  const messageMapping = (shouldListen && shopId && `shops.${shopId}.orders.events`) || null;

  return useRSocket<OrderSavedEvent>(messageMapping, session);
};

/**
 * Order checkout accessories return api hook
 */
export const useCheckoutAccessoriesReturnForOrder = (): AxiosHookResponse<CheckoutAccessoriesReturnParam, void> => {
  const url = `${API_SHOP_OPERATIONS_BASE_URL}/${SHOP_OPERATIONS_SHOPS_URL}/orders/checkout-accessories-return`;

  return useAxiosPostRequest(url);
};
